/**
 * Wizard de Onboarding para Suscripciones
 *
 * Se muestra cuando un usuario nuevo no tiene suscripción activa
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../context/SubscriptionContext';
import { getPlans, getPublicPartnerCode } from '../services/api';
import { getStoredPromoAttribution, setStoredPromoAttribution } from '../utils/promoAttribution';
import { resolveInitialPlanId } from '../utils/planPreselection';
import { DEFAULT_TRIAL_DAYS, getOfferedPlans, getTrialDays, hasTrial } from '../utils/plans';
import PlanCardsGrid from './PlanCardsGrid';
import './OnboardingWizard.css';

// RES-99: espera antes de consultar el código mientras el usuario tipea.
export const PARTNER_CODE_DEBOUNCE_MS = 400;
const PARTNER_CODE_RE = /^[A-Z0-9]{4,20}$/;

function OnboardingWizard({ isOpen, onComplete }) {
  const { t } = useTranslation();
  const { createSubscription } = useSubscription();

  // Promo code de partner (NER-3079): prefill desde el link/cookie, editable.
  // Untouched → queda como 'cookie'; al editarlo lo marcamos 'code' (manual).
  const [promoCode, setPromoCode] = useState(() => getStoredPromoAttribution()?.code || '');

  const [step, setStep] = useState(1); // 1: Welcome, 2: Plan Selection, 3: Confirming
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [error, setError] = useState(null);
  // RES-99: resultado de consultar el código de partner.
  // null = sin código / sin respuesta; { valid: true, partnerName, trialDays } | { valid: false }.
  const [partnerTrial, setPartnerTrial] = useState(null);

  // RES-99: al abrir y cada vez que cambia el código (con debounce), consultar si es de un
  // partner activo para mostrar "60 días gratis con el código de X". Nunca bloquea el alta:
  // el código se sigue mandando igual al crear la suscripción y el backend decide.
  useEffect(() => {
    const code = (promoCode || '').trim().toUpperCase();
    // Al cambiar el código se descarta el resultado anterior (no mostrar el partner viejo)
    setPartnerTrial(null);
    if (!isOpen || !code) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      if (!PARTNER_CODE_RE.test(code)) {
        setPartnerTrial({ valid: false });
        return;
      }
      try {
        const result = await getPublicPartnerCode(code);
        if (cancelled) return;
        setPartnerTrial(
          result?.valid
            ? {
                valid: true,
                partnerName: result.partnerName || code,
                trialDays: Number(result.trialDays) || 60,
              }
            : { valid: false }
        );
      } catch (err) {
        if (cancelled) return;
        // 404 = código inexistente → ayuda neutra; otro error (red) → no mostrar nada.
        setPartnerTrial(err?.response?.status === 404 ? { valid: false } : null);
      }
    }, PARTNER_CODE_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [promoCode, isOpen]);

  const partnerTrialDays = partnerTrial?.valid ? partnerTrial.trialDays : null;
  // Con código válido las cards muestran los días de la prueba extendida (solo display).
  const displayPlans = partnerTrialDays
    ? plans.map((p) => (getTrialDays(p) > 0 ? { ...p, trialDays: partnerTrialDays } : p))
    : plans;

  // Cargar planes disponibles al montar el componente
  useEffect(() => {
    if (isOpen) {
      loadPlans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadPlans = async () => {
    try {
      setLoadingPlans(true);
      // Consumir el plan preseleccionado desde la landing (?plan=X → RegisterPage).
      // Se lee y limpia acá (no en un useEffect de mount) porque el wizard está
      // siempre montado con isOpen togglendo de false→true: un cleanup en mount
      // borraría el valor antes de que loadPlans llegue a leerlo.
      const preselectedPlanId = sessionStorage.getItem('preselectedPlan');
      if (preselectedPlanId) {
        sessionStorage.removeItem('preselectedPlan');
      }
      const response = await getPlans();
      console.log('[OnboardingWizard] Planes cargados:', response);

      if (response.plans) {
        // Solo planes de la oferta vigente (sin FREE ni Business).
        const sorted = getOfferedPlans(response.plans);
        setPlans(sorted);
        // Pre-select: plan de la landing si es válido, luego professional, luego el primero.
        setSelectedPlan(resolveInitialPlanId(sorted, preselectedPlanId));
      }
    } catch (err) {
      console.error('[OnboardingWizard] Error cargando planes:', err);
      setError(t('onboarding.errorLoadingPlans'));
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleNext = () => {
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleConfirm = async () => {
    if (!selectedPlan) {
      setError(t('onboarding.selectPlanError'));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Starter y Profesional arrancan con prueba gratis (30 días; 60 con código de partner
      // válido, lo resuelve el backend con el promoCode que ya mergea createSubscription).
      const plan = plans.find((p) => p.planId === selectedPlan) || selectedPlan;
      const startTrial = hasTrial(plan);

      console.log('[OnboardingWizard] Creando suscripción:', { selectedPlan, startTrial });

      const result = await createSubscription(selectedPlan, startTrial);

      if (result.success) {
        console.log('[OnboardingWizard] Suscripción creada exitosamente');
        // Esperar un momento para mostrar éxito antes de cerrar
        setTimeout(() => {
          onComplete();
        }, 1500);
      } else {
        setError(result.error || t('onboarding.errorCreatingSubscription'));
      }
    } catch (err) {
      console.error('[OnboardingWizard] Error creando suscripción:', err);
      setError(err.message || t('onboarding.errorCreatingSubscription'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="onboarding-overlay">
      <div className="onboarding-container">
        {/* Step 1: Welcome */}
        {step === 1 && (
          <div className="onboarding-step onboarding-welcome">
            <div className="welcome-icon">🎉</div>
            <h1>{t('onboarding.welcome')}</h1>
            <p className="welcome-subtitle">{t('onboarding.welcomeSubtitle')}</p>

            <div className="welcome-content">
              <div className="welcome-section">
                <span className="welcome-emoji">🍽️</span>
                <h3>{t('onboarding.feature1Title')}</h3>
                <p>{t('onboarding.feature1Description')}</p>
              </div>

              <div className="welcome-section">
                <span className="welcome-emoji">📊</span>
                <h3>{t('onboarding.feature2Title')}</h3>
                <p>{t('onboarding.feature2Description')}</p>
              </div>

              <div className="welcome-section">
                <span className="welcome-emoji">💰</span>
                <h3>{t('onboarding.feature3Title')}</h3>
                <p>{t('onboarding.feature3Description')}</p>
              </div>
            </div>

            <div className="welcome-trial-banner">
              <p className="trial-banner-text" data-testid="onboarding-trial-banner">
                ✨{' '}
                {partnerTrialDays
                  ? t('onboarding.partnerTrial.banner', {
                      partnerName: partnerTrial.partnerName,
                      days: partnerTrialDays,
                    })
                  : t('onboarding.trialBanner')}
              </p>
            </div>

            <div className="onboarding-actions">
              <button type="button" className="btn btn-primary btn-large" onClick={handleNext}>
                {t('onboarding.getStarted')}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Plan Selection */}
        {step === 2 && (
          <div className="onboarding-step onboarding-plans">
            <h2>{t('onboarding.selectYourPlan')}</h2>
            <p className="plans-subtitle">{t('onboarding.selectPlanSubtitle')}</p>

            {loadingPlans ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>{t('onboarding.loadingPlans')}</p>
              </div>
            ) : (
              <PlanCardsGrid
                plans={displayPlans}
                selectedPlanId={selectedPlan}
                onSelect={setSelectedPlan}
              />
            )}

            <div className="onboarding-promo">
              <label htmlFor="onboarding-promo-code">
                {t('onboarding.promoCodeLabel', 'Código de promoción (opcional)')}
              </label>
              <input
                id="onboarding-promo-code"
                type="text"
                maxLength={20}
                value={promoCode}
                placeholder={t('onboarding.promoCodePlaceholder', 'Ej: NARDA20')}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  setPromoCode(value);
                  // Edición manual → atribución fuente 'code'.
                  setStoredPromoAttribution(value, 'code');
                }}
              />
              <span className="onboarding-promo-hint">
                {t(
                  'onboarding.promoCodeHint',
                  'Si llegaste desde el link de un partner, tu código ya está cargado.'
                )}
              </span>
              {partnerTrial?.valid && (
                <div
                  className="onboarding-partner-trial onboarding-partner-trial--valid"
                  role="status"
                  data-testid="partner-trial-valid"
                >
                  <strong>
                    {t('onboarding.partnerTrial.valid', {
                      partnerName: partnerTrial.partnerName,
                      days: partnerTrial.trialDays,
                    })}
                  </strong>
                  <span>
                    {t('onboarding.partnerTrial.normalTrial', { days: DEFAULT_TRIAL_DAYS })}
                  </span>
                </div>
              )}
              {partnerTrial && !partnerTrial.valid && (
                <div
                  className="onboarding-partner-trial onboarding-partner-trial--invalid"
                  role="status"
                  data-testid="partner-trial-invalid"
                >
                  {t('onboarding.partnerTrial.invalid', { days: DEFAULT_TRIAL_DAYS })}
                </div>
              )}
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="onboarding-actions">
              <button type="button" className="btn btn-secondary" onClick={handleBack}>
                {t('onboarding.back')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConfirm}
                disabled={loading || !selectedPlan || plans.length === 0}
              >
                {loading ? t('onboarding.creating') : t('onboarding.confirmAndStart')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default OnboardingWizard;
